import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type InfluencerUpdate = Database['public']['Tables']['influencers']['Update'];

export interface InfluencerHandleValidationResult {
  validHandles: string[];
  invalidHandles: string[];
  invalidReasons: Record<string, string>;
  profiles: ValidatedInfluencerProfile[];
}

export interface ValidatedInfluencerProfile {
  handle: string;
  name: string;
  followersCount: number | null;
  followingCount: number | null;
  profilePicUrl: string | null;
  bio: string | null;
  email: string | null;
  businessCategory: string | null;
  engagementRate: number | null;
  avgLikes: number | null;
  videoViewCount: number | null;
  verified: boolean | null;
  instagramPk: number | null;
}

interface ValidateInfluencerHandlesResponse {
  validHandles?: unknown;
  invalidHandles?: unknown;
  invalidReasons?: unknown;
  profiles?: unknown;
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isStringRecord = (value: unknown): value is Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every((item) => typeof item === 'string');
};

const isNullableNumber = (value: unknown): value is number | null =>
  value === null || typeof value === 'number';

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isNullableBoolean = (value: unknown): value is boolean | null =>
  value === null || typeof value === 'boolean';

const isValidatedInfluencerProfile = (value: unknown): value is ValidatedInfluencerProfile => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const profile = value as Record<string, unknown>;

  return (
    typeof profile.handle === 'string' &&
    typeof profile.name === 'string' &&
    isNullableNumber(profile.followersCount) &&
    isNullableNumber(profile.followingCount) &&
    isNullableString(profile.profilePicUrl) &&
    isNullableString(profile.bio) &&
    isNullableString(profile.email) &&
    isNullableString(profile.businessCategory) &&
    isNullableNumber(profile.engagementRate) &&
    isNullableNumber(profile.avgLikes) &&
    isNullableNumber(profile.videoViewCount) &&
    isNullableBoolean(profile.verified) &&
    isNullableNumber(profile.instagramPk)
  );
};

const isValidatedInfluencerProfiles = (value: unknown): value is ValidatedInfluencerProfile[] =>
  Array.isArray(value) && value.every(isValidatedInfluencerProfile);

export const formatInvalidInfluencerHandlesMessage = (handles: string[]): string => {
  const displayHandles = handles.map((handle) => `@${handle.replace(/^@/, '')}`).join(', ');

  return handles.length === 1
    ? `We couldn't find an Instagram account for ${displayHandles}. Please check the username and try again.`
    : `We couldn't find Instagram accounts for ${displayHandles}. Please check the usernames and try again.`;
};

export const validateInfluencerHandles = async (
  handles: string[],
  options: { includeMedia?: boolean } = {}
): Promise<InfluencerHandleValidationResult> => {
  const { data, error } = await supabase.functions.invoke<ValidateInfluencerHandlesResponse>(
    'validate-influencer-handles',
    {
      body: { handles, includeMedia: options.includeMedia ?? true },
    }
  );

  if (error) {
    throw new Error(
      error.message ||
        "We couldn't verify the Instagram usernames right now. Please try again in a moment."
    );
  }

  if (
    !data ||
    !isStringArray(data.validHandles) ||
    !isStringArray(data.invalidHandles) ||
    !isValidatedInfluencerProfiles(data.profiles)
  ) {
    throw new Error("We couldn't verify the influencer details right now. Please try again.");
  }

  return {
    validHandles: data.validHandles,
    invalidHandles: data.invalidHandles,
    invalidReasons: isStringRecord(data.invalidReasons) ? data.invalidReasons : {},
    profiles: data.profiles,
  };
};

export const toInfluencerProfileUpdate = (
  profile: ValidatedInfluencerProfile,
  options: { preserveMetrics?: boolean } = {}
): InfluencerUpdate => ({
  name: profile.name,
  followers_count: profile.followersCount,
  following_count: profile.followingCount,
  profile_pic_url: profile.profilePicUrl,
  bio: profile.bio,
  email: profile.email,
  business_category: profile.businessCategory,
  ...(options.preserveMetrics
    ? {}
    : {
        engagement_rate: profile.engagementRate,
        avg_likes: profile.avgLikes,
        video_view_count: profile.videoViewCount,
      }),
  verified: profile.verified,
  instagram_pk: profile.instagramPk,
  enrichment_status: 'completed',
  updated_at: new Date().toISOString(),
  last_refreshed_at: new Date().toISOString(),
});
